import { Container, Box, Flex, Grid } from 'components/box';
import { Text, Heading } from 'components/typography';
import RichText from 'components/rich-text';
import { styledCms } from 'shared/style';
import { Link } from 'components/configurable-routing';
import cmsPageQuery from 'queries/cmsPage.graphql';
import { css } from '@emotion/react';
import { PageProps, QueryCmsPageOutput, CmsPageInterface } from 'types/types';
import { fetchGraphQl, parseQueryAST } from '../../util/graphql';
import { logError } from '../../util/error-logger';
import resourceUrl from '../../util/make-url';
import config from '../../util/load-config';
import { prepMetaTags } from '../../util/seo';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { cacheControl } from 'constants/cache-control';
import { logInvalidJSON } from '../../util/log-invalid-json';
//import RelatedProducts from 'components/_pages/products/related-products';

export interface CmsPageProps {
  page: CmsPageInterface;
}

const ICON_WIDTH = 210;

// below should be the URL key as a string found in Magento
const CONTENT_SLUG = 'brands';
interface BrandImageCovers {
  id: string;
  label: string;
  imageUrl: string;
}

const brandImageCovers: BrandImageCovers[] = [
  {
    id: 'philips',
    label: 'Philips',
    imageUrl: resourceUrl('odo/brands/landing-page/philips.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'the-harvest-table',
    label: 'The Harvest Table',
    imageUrl: resourceUrl('odo/brands/landing-page/the-harvest-table.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'crocs',
    label: 'Crocs',
    imageUrl: resourceUrl('odo/brands/landing-page/crocs.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'smeg',
    label: 'Smeg',
    imageUrl: resourceUrl('odo/brands/landing-page/smeg.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'asics',
    label: 'ASICS',
    imageUrl: resourceUrl('odo/brands/landing-page/asics.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'fossil',
    label: 'Fossil',
    imageUrl: resourceUrl('odo/brands/landing-page/fossil.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'dyson',
    label: 'Dyson',
    imageUrl: resourceUrl('odo/brands/landing-page/dyson.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'ugg',
    label: 'UGG',
    imageUrl: resourceUrl('odo/brands/landing-page/ugg.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'weber',
    label: 'Weber',
    imageUrl: resourceUrl('odo/brands/landing-page/weber.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'boerboel-wear',
    label: 'Boerboel Wear',
    imageUrl: resourceUrl('odo/brands/landing-page/boerboel-wear.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
  {
    id: 'saucony',
    label: 'Saucony',
    imageUrl: resourceUrl('odo/brands/landing-page/saucony.jpg', {
      width: ICON_WIDTH,
      isAssetUrl: true,
    }),
  },
];

const BrandsLandingPage = ({ page }: CmsPageProps) => (
  <Container>
    <Box>
      <Heading as="h2" color="black" marginBottom={3} lineHeight={1.5}>
        {page.contentHeading}
      </Heading>

      <RichText
        css={theme => styledCms(theme, { highlightedBackground: false })}
        content={page.content}
      />
    </Box>

    <Grid
      gridTemplateColumns={[
        'repeat(2, 1fr)',
        'repeat(3, 1fr)',
        'repeat(4, 1fr)',
      ]}
      gridColumnGap={4}
      gridRowGap={4}
    >
      {brandImageCovers.map(brand => (
        <Link
          key={brand.id}
          href={`/brands/${brand.id}`}
          dynamicUrl="/brands/[id]"
        >
          <Flex
            maxWidth="100%"
            flexDirection="column"
            alignItems="left"
            css={css`
              gap: 10px;
            `}
          >
            <img
              css={css`
                width: ${ICON_WIDTH}px;
                max-width: 100%;
                border-radius: 6px;
              `}
              src={brand.imageUrl}
              alt={brand.label}
            />
            <Text fontWeight="bold">{brand.label}</Text>
          </Flex>
        </Link>
      ))}
    </Grid>
    {/*TODO: Uncomment when UNBXD is implemented - https://onedayonly.atlassian.net/browse/FE-1260*/}
    {/* <RelatedProducts customTitle="Products you might like" hideRelated /> */}
  </Container>
);

export async function getServerSideProps({
  res,
}: GetServerSidePropsContext): Promise<
  GetServerSidePropsResult<CmsPageProps & PageProps>
> {
  try {
    const [query, operationName] = await parseQueryAST(
      cmsPageQuery,
      CONTENT_SLUG
    );

    const { data, errors } = await fetchGraphQl<QueryCmsPageOutput>({
      operationName,
      query,
      variables: { identifier: CONTENT_SLUG },
      logInvalidJSON,
    });

    // only fail if there is no data
    if (!data || !data.page) {
      // log error
      if (errors && errors.length > 0) {
        const [firstError] = errors;
        logError(
          firstError.message,
          `brands-landing-page.query.${CONTENT_SLUG}`
        );
      }

      // returns fallback content in the case of an error
      return {
        props: {
          page: {
            title: config.brandPageFallbacks.fallbackTitle,
            contentHeading: config.brandPageFallbacks.fallbackHeading,
            content: config.brandPageFallbacks.fallbackContent,
            metaKeywords: config.brandPageFallbacks.fallbackKeywords,
            metaDescription: config.brandPageFallbacks.fallbackDescription,
            isActive: true,
          },
        },
      };
    }

    res.setHeader('cache-control', cacheControl.dynamic);

    const meta = config.seo.meta.pages[CONTENT_SLUG];

    return {
      props: {
        ...(meta ? { layout: { metaTags: prepMetaTags(meta) } } : {}),
        page: data.page,
      },
    };
  } catch (e) {
    logError(e, `brands-landing-page.catch.${CONTENT_SLUG}`);
    return { notFound: true };
  }
}
export default BrandsLandingPage;
